import jsPDF from 'jspdf';

/** Data for a single payslip (for PDF or print). */
export interface PayslipData {
  staffName: string;
  payPeriodStart: string;
  payPeriodEnd: string;
  hours: number;
  hourlyRate: number;
  gross: number;
  paye: number;
  net: number;
  /** Holiday / annual leave pay (8% line) if applicable */
  holidayPay?: number;
  taxCode?: string;
  businessName?: string;
  businessAddress?: string;
  businessGstNumber?: string;
  businessNzbn?: string;
  /** Optional base64 data URL for logo image (PNG/JPEG) */
  logoDataUrl?: string;
  /** Optional logo aspect ratio (width / height) so we can keep the logo undistorted */
  logoAspectRatio?: number;
}

/**
 * Generate a one-page PDF for a single payslip.
 */
export function generatePayslipPdf(data: PayslipData, filename?: string): void {
  const doc = new jsPDF({ orientation: 'portrait' });
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = margin;

  // Branded header bar
  const headerHeight = 26;
  doc.setFillColor(236, 72, 153); // pink brand colour
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Optional logo on top-right, constrained inside the pink header (no overflow),
  // scaled by height while keeping the original aspect ratio.
  if (data.logoDataUrl) {
    const logoMaxHeight = headerHeight - 8; // 8pt padding from top and bottom of header
    const aspect =
      data.logoAspectRatio && data.logoAspectRatio > 0 ? data.logoAspectRatio : 1;
    const logoHeight = logoMaxHeight;
    const logoWidth = logoMaxHeight * aspect;
    const logoY = (headerHeight - logoHeight) / 2; // vertically centred in header
    const logoX = pageWidth - margin - logoWidth;
    try {
      doc.addImage(data.logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch {
      // Ignore logo errors so PDF generation still succeeds
    }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  const businessName = data.businessName || 'KINGS PARK';
  doc.text(businessName, margin, 12);

  doc.setFontSize(9);
  if (data.businessAddress) {
    doc.text(data.businessAddress, margin, 17);
  }
  if (data.businessGstNumber) {
    doc.text(`GST #${data.businessGstNumber}`, margin, 21);
  }
  if (data.businessNzbn) {
    doc.text(`NZBN ${data.businessNzbn}`, margin, 25);
  }

  doc.setTextColor(0, 0, 0);
  y = headerHeight + 10;

  // Pay period + employee summary
  doc.setFontSize(10);
  doc.text(`Pay period: ${data.payPeriodStart} to ${data.payPeriodEnd}`, margin, y);
  if (data.taxCode) {
    doc.text(`Tax code: ${data.taxCode}`, pageWidth - margin - 40, y);
  }
  y += 6;
  doc.text(`Employee: ${data.staffName}`, margin, y);
  y += 8;

  doc.setDrawColor(200, 200, 200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  // Earnings section
  doc.setFontSize(11);
  doc.setFont(undefined as any, 'bold');
  doc.text('Earnings', margin, y);
  y += 6;

  const earningsRows: [string, string][] = [
    ['Hours worked', data.hours.toFixed(2)],
    ['Hourly rate', `$${data.hourlyRate.toFixed(2)}`],
    ['Ordinary pay', `$${(data.hours * data.hourlyRate).toFixed(2)}`],
  ];
  if (data.holidayPay != null && data.holidayPay > 0) {
    earningsRows.push(['Holiday pay', `$${data.holidayPay.toFixed(2)}`]);
  }
  earningsRows.push(['Gross pay', `$${data.gross.toFixed(2)}`]);

  earningsRows.forEach(([label, value]) => {
    doc.setFont(undefined as any, 'normal');
    doc.text(label, margin, y);
    doc.setFont(undefined as any, 'bold');
    doc.text(value, 120, y);
    y += 6;
  });

  y += 4;
  doc.setDrawColor(220, 220, 220);
  doc.line(margin, y, 200, y);
  y += 6;

  // Deductions section
  doc.setFontSize(11);
  doc.setFont(undefined as any, 'bold');
  doc.text('Deductions', margin, y);
  y += 6;

  doc.setFont(undefined as any, 'normal');
  doc.text('PAYE (IRD)', margin, y);
  doc.setFont(undefined as any, 'bold');
  doc.text(`$${data.paye.toFixed(2)}`, 120, y);
  y += 8;

  // Net pay
  doc.setDrawColor(0, 0, 0);
  doc.line(margin, y, 200, y);
  y += 7;
  doc.setFontSize(12);
  doc.setFont(undefined as any, 'bold');
  doc.text('Net pay', margin, y);
  doc.text(`$${data.net.toFixed(2)}`, 120, y);

  // Footer note (confidentiality + system generated)
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - 22;
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(
    'This is a system-generated payslip and does not require a signature. The information contained herein is confidential. If you have any queries, please contact the payroll department immediately on payroll@kingspark.co.nz.',
    margin,
    footerY,
    { maxWidth: pageWidth - margin * 2 }
  );

  doc.save(filename || `payslip-${data.staffName.replace(/\s+/g, '-')}-${data.payPeriodStart}.pdf`);
}

/**
 * Generate a multi-page PDF with one payslip per page (batch).
 */
export function generatePayslipsBatchPdf(list: PayslipData[], businessName?: string, filename?: string): void {
  if (list.length === 0) return;
  const doc = new jsPDF({ orientation: 'portrait' });
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();

  list.forEach((data, index) => {
    if (index > 0) doc.addPage();
    let y = margin;
    const pageHeight = doc.internal.pageSize.getHeight();

    // Branded header
    const headerHeight = 26;
    doc.setFillColor(236, 72, 153);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');

    // Optional logo, constrained inside the pink header, keeping original aspect ratio
    if (data.logoDataUrl) {
      const logoMaxHeight = headerHeight - 8;
      const aspect =
        data.logoAspectRatio && data.logoAspectRatio > 0 ? data.logoAspectRatio : 1;
      const logoHeight = logoMaxHeight;
      const logoWidth = logoMaxHeight * aspect;
      const logoY = (headerHeight - logoHeight) / 2;
      const logoX = pageWidth - margin - logoWidth;
      try {
        doc.addImage(data.logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
      } catch {
        // ignore logo errors
      }
    }

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    const bn = businessName || data.businessName || 'KINGS PARK';
    doc.text(bn, margin, 12);

    doc.setFontSize(9);
    if (data.businessAddress) {
      doc.text(data.businessAddress, margin, 17);
    }
    if (data.businessGstNumber) {
      doc.text(`GST #${data.businessGstNumber}`, margin, 21);
    }
    if (data.businessNzbn) {
      doc.text(`NZBN ${data.businessNzbn}`, margin, 25);
    }

    doc.setTextColor(0, 0, 0);
    y = headerHeight + 10;

    doc.setFontSize(10);
    doc.text(`Pay period: ${data.payPeriodStart} to ${data.payPeriodEnd}`, margin, y);
    if (data.taxCode) doc.text(`Tax code: ${data.taxCode}`, pageWidth - margin - 40, y);
    y += 6;
    doc.text(`Employee: ${data.staffName}`, margin, y);
    y += 8;

    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    // Earnings
    doc.setFontSize(11);
    doc.setFont(undefined as any, 'bold');
    doc.text('Earnings', margin, y);
    y += 6;

    const earningsRows: [string, string][] = [
      ['Hours worked', data.hours.toFixed(2)],
      ['Hourly rate', `$${data.hourlyRate.toFixed(2)}`],
      ['Ordinary pay', `$${(data.hours * data.hourlyRate).toFixed(2)}`],
    ];
    if (data.holidayPay != null && data.holidayPay > 0) {
      earningsRows.push(['Holiday pay', `$${data.holidayPay.toFixed(2)}`]);
    }
    earningsRows.push(['Gross pay', `$${data.gross.toFixed(2)}`]);

    earningsRows.forEach(([label, value]) => {
      doc.setFont(undefined as any, 'normal');
      doc.text(label, margin, y);
      doc.setFont(undefined as any, 'bold');
      doc.text(value, 120, y);
      y += 6;
    });

    y += 4;
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y, 200, y);
    y += 6;

    // Deductions
    doc.setFontSize(11);
    doc.setFont(undefined as any, 'bold');
    doc.text('Deductions', margin, y);
    y += 6;

    doc.setFont(undefined as any, 'normal');
    doc.text('PAYE (IRD)', margin, y);
    doc.setFont(undefined as any, 'bold');
    doc.text(`$${data.paye.toFixed(2)}`, 120, y);
    y += 8;

    // Net pay
    doc.setDrawColor(0, 0, 0);
    doc.line(margin, y, 200, y);
    y += 7;
    doc.setFontSize(12);
    doc.setFont(undefined as any, 'bold');
    doc.text('Net pay', margin, y);
    doc.text(`$${data.net.toFixed(2)}`, 120, y);

    // Footer note
    const footerY = pageHeight - 22;
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(
      'This is a system-generated payslip and does not require a signature. The information contained herein is confidential. If you have any queries, please contact the payroll department immediately on payroll@kingspark.co.nz.',
      margin,
      footerY,
      { maxWidth: pageWidth - margin * 2 }
    );
  });

  doc.save(filename || `payslips-${list[0].payPeriodStart}-batch.pdf`);
}
